import { useEffect, useRef, useState } from "react";
import type { CustomerOrder } from "../../api/types";
import { useOrderReferencePhotos } from "../orders/useOrderReferencePhotos";
import { useT } from "../../i18n/LanguageProvider";

interface Props {
  order: CustomerOrder;
  /** Items in the bouquet the florist is building right now. */
  includedItemIds: ReadonlySet<string>;
  /** Requested items this shop has no stock of. */
  unavailableItemIds: ReadonlySet<string>;
  /** Paper and ribbon items — their requested quantity is centimetres. */
  cmItemIds: ReadonlySet<string>;
}

/**
 * The manager's brief for an order-linked floor build: order code, customer,
 * the described bouquet and its reference photos.
 *
 * Why it is a strip plus an overlay, and not the card it used to be: the card
 * was a sibling ABOVE `.sf-bouquet-body`, so its ~460 px (a 260 px photo plus
 * card padding and margins) came off the top of BOTH columns — leaving the
 * catalog roughly one row of flowers and clipping the bouquet panel's totals.
 * That is the same mistake `.sf-bouquet-filter-panel` made until the owner's
 * 2026-08-01 relayout moved it inside the catalog column; the comment on that
 * rule in base.css explains it. The strip costs ~56 px and the body grid keeps
 * the full height of the device.
 *
 * The overlay opens ONCE by itself per order (owner decision 2026-08-02) —
 * the old card was unmissable and a florist must still see what the manager
 * asked for without hunting for it — and can then be closed and reopened at
 * will from "See description".
 *
 * The manager's components are listed with a live status (owner 2026-10-16,
 * ORD-2026-00011): they are a suggestion the florist may depart from, so the
 * florist has to be able to SEE them — the order used to say "only requested
 * components" while showing none of them.
 */
export function ShopFloorOrderBrief({
  order,
  includedItemIds,
  unavailableItemIds,
  cmItemIds,
}: Props) {
  const { t } = useT();
  const urls = useOrderReferencePhotos(order.id, "floor");
  const [open, setOpen] = useState(false);
  const autoOpenedFor = useRef<string | null>(null);

  const notes = order.bouquetNotes?.trim() ?? "";
  const components = order.lines.filter((line) => line.role === "BOUQUET_COMPONENT");
  const comparable = components.filter((line) => line.itemId);
  const includedCount = comparable.filter((line) =>
    includedItemIds.has(line.itemId as string),
  ).length;
  const countLabel =
    comparable.length > 0
      ? t("orderBrief.includedCount", {
          included: includedCount,
          total: comparable.length,
        })
      : "";
  // The server's own count, so the auto-open decision does not wait for the
  // photo bytes to arrive; the images then populate the open overlay.
  const hasBrief =
    notes.length > 0 || order.referencePhotoCount > 0 || components.length > 0;

  useEffect(() => {
    if (!hasBrief || autoOpenedFor.current === order.id) return;
    autoOpenedFor.current = order.id;
    setOpen(true);
  }, [order.id, hasBrief]);

  return (
    <>
      <div className="sf-order-strip">
        {urls.length > 0 ? (
          <button
            type="button"
            className="sf-order-strip-thumb-btn"
            onClick={() => setOpen(true)}
            aria-label={t("orderBrief.openDescription")}
          >
            <img src={urls[0]} alt="" className="sf-order-strip-thumb" />
          </button>
        ) : null}
        <div className="sf-order-strip-text">
          <span className="sf-order-strip-title">
            {order.code} · {order.customerName}
          </span>
          {notes || countLabel ? (
            <span className="sf-order-strip-note muted">
              {[countLabel, notes].filter(Boolean).join(" · ")}
            </span>
          ) : null}
        </div>
        {/* No dead button: with neither a description nor a photo there is
            nothing to open. */}
        {hasBrief ? (
          <button
            type="button"
            className="button tinted sf-order-strip-btn"
            onClick={() => setOpen(true)}
          >
            {t("orderBrief.seeDescription")}
          </button>
        ) : null}
      </div>

      {open ? (
        <div
          className="sf-bouquet-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label={t("orderBrief.dialogLabel")}
          onClick={() => setOpen(false)}
        >
          <div
            className="sf-bouquet-modal sf-order-brief-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h4 className="sf-bouquet-modal-title">
              {order.code} · {order.customerName}
            </h4>
            {notes ? <p className="sf-order-brief-notes">{notes}</p> : null}
            {components.length > 0 ? (
              <div className="sf-order-brief-components">
                <strong>{t("orderBrief.requestedComponents")}</strong>
                {countLabel ? <span className="muted">{countLabel}</span> : null}
                <ul>
                  {components.map((line) => {
                    const itemId = line.itemId;
                    const qty = Number(line.quantity);
                    return (
                      <li key={line.id}>
                        <span className="sf-order-brief-component-name">
                          {line.itemName ?? line.description}
                        </span>
                        <span className="muted">
                          {itemId && cmItemIds.has(itemId)
                            ? t("orderBrief.cm", { cm: qty })
                            : t("orderBrief.qty", { qty })}
                        </span>
                        {itemId ? (
                          includedItemIds.has(itemId) ? (
                            <span className="badge success">
                              {t("orderBrief.statusIncluded")}
                            </span>
                          ) : unavailableItemIds.has(itemId) ? (
                            <span className="badge danger">
                              {t("orderBrief.statusUnavailable")}
                            </span>
                          ) : (
                            <span className="badge warning">
                              {t("orderBrief.statusMissing")}
                            </span>
                          )
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null}
            {urls.length > 0 ? (
              <div className="sf-order-reference-photos">
                {urls.map((url, index) => (
                  <img
                    key={url}
                    src={url}
                    alt={t("orderBrief.referencePhoto", { number: index + 1 })}
                    className="sf-order-brief-photo"
                  />
                ))}
              </div>
            ) : null}
            <button
              type="button"
              className="button plain"
              onClick={() => setOpen(false)}
            >
              {t("common.close")}
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}
